import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { VendorScoreHistory } from '@/services/vendorScoreService';
import { cleanDateAndTimeObject } from '../../shared/renderDate';
import { useTheme } from '@/contexts/ThemeContext';
import { colorValues } from '@/contexts/ThemeContext';

interface VendorScoreChartProps {
  history: VendorScoreHistory[];
  domain: string;
}

export const VendorScoreChart: React.FC<VendorScoreChartProps> = ({ history, domain }) => {
  const theme = useTheme();
  const primaryColor = colorValues[theme.primaryColor]?.[theme.isDark ? 'dark' : 'light']?.primary || '#2563eb';

  // Transform history data for the chart
  const chartData = React.useMemo(() => {
    return history
      .map(item => ({
        date: cleanDateAndTimeObject(item.captureDate),
        score: item.capturedScore,
        formattedDate: cleanDateAndTimeObject(item.captureDate)?.toLocaleDateString() || 'Unknown'
      }))
      .filter(item => item.date && item.score !== undefined)
      .sort((a, b) => a.date!.getTime() - b.date!.getTime())
      .map(item => ({
        date: item.date!.toLocaleDateString(),
        score: item.score,
        fullDate: item.formattedDate
      }));
  }, [history]);

  if (!chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendor Security Score History</CardTitle>
          <CardDescription>No historical data available for {domain}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data to display
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vendor Security Score History</CardTitle>
        <CardDescription>Security score trend for {domain}</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[0, 1000]}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: number) => [`${value}/1000`, 'Score']}
              labelFormatter={(label) => `Date: ${label}`}
            />
            <Area
              type="monotone"
              dataKey="score"
              stroke={primaryColor}
              strokeWidth={2}
              fill={primaryColor}
              fillOpacity={0.2}
              dot={{ fill: primaryColor, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: primaryColor, strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
